import { useState, useCallback } from "react"
import { getBaseUrl } from "../utils/deploymentUtils"
import { protectedResources } from "../authConfig"
import { useMemo } from "react"
// eslint-disable-next-line no-unused-vars
import { useMsal } from "@azure/msal-react"
import { InteractionRequiredAuthError } from "@azure/msal-browser"
import { loginRequest } from "../authConfig"

const useFetchWithMsal = (msalRequest) => {
  const { instance } = useMsal() // Handles login logout and token acquisition
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [data, setData] = useState(null)

  const isInsideIframe = window.parent !== window

  // helper function to get the active account or the first account if none is active
  // this is important to ensure that the user is signed in before making api calls
  const getValidAccount = useCallback(() => {
    let account = instance.getActiveAccount()
    if (!account) {
      const allAccounts = instance.getAllAccounts()
      if (allAccounts.length > 0) {
        account = allAccounts[0]
        instance.setActiveAccount(account)
      } else {
        return null
      }
    }
    return account
  }, [instance])

  //caching the msalRequest to avoid unnecessary re-renders (eslint error)
  //If msalRequest isn’t provided, it uses a default set of scopes
  const msalRequestDefault = useMemo(() => {
    return (
      msalRequest ?? {
        scopes: protectedResources.apiTodoList.scopes.read,
      }
    )
  }, [msalRequest])

  /**
   * Execute a fetch request with the given options
   * @param {string} method: GET, POST, PUT, DELETE
   * @param {String} endpoint: The endpoint to call
   * @param {Object} data: The data to send to the endpoint, if any
   * @returns JSON response
   */
  const execute = async (method, endpoint, data = null) => {
    setIsLoading(true)
    setError(null)

    try {
      // Check if the user is signed in
      const account = getValidAccount()
      if (!account) {
        if (!isInsideIframe) {
          await instance
            .loginRedirect(loginRequest)
            .catch((error) => console.log(error))
        }
      } else {
        //Acquire token silently if error it is stored in InteractionRequiredAuthError
        const tokenResponse = await instance.acquireTokenSilent({
          ...msalRequestDefault,
          account,
        })

        // Perform the fetch request
        // Set the headers for the request, including the Authorization header with the token
        const headers = new Headers()
        const bearer = `Bearer ${tokenResponse.accessToken}`
        headers.append("Authorization", bearer)
        if (data) headers.append("Content-Type", "application/json")

        let options = {
          method: method,
          headers: headers,
          body: data ? JSON.stringify(data) : null,
        }

        console.log("calling: " + getBaseUrl() + endpoint)

        const response = await fetch(getBaseUrl() + endpoint, options)
        const responseData = await response.json()
        setData(responseData)

        return responseData
      }
    } catch (err) {
      // Handle token acquisition or fetch errors
      if (err instanceof InteractionRequiredAuthError) {
        // If the token acquisition fails, redirect the user to sign in
        try {
          if (!isInsideIframe) {
            await instance.acquireTokenRedirect({
              ...msalRequestDefault,
              account: instance.getActiveAccount(),
              redirectUri: "/", // Redirect to the current page
            })
          }
        } catch (redirectError) {
          // If the user cant re-signin, throw the error
          setError(redirectError)
          throw redirectError
        }
      } else {
        setError(err)
        throw err
      }
    } finally {
      setIsLoading(false)
    }
  }

  return {
    isLoading,
    error,
    data,
    execute: useCallback(execute, [
      instance,
      msalRequestDefault,
      getValidAccount,
      isInsideIframe,
    ]), // to avoid infinite calls when inside a `useEffect`
  }
}

export default useFetchWithMsal
